import type {VueElement} from 'vue';

/**
 * Formats a given number string into a number string with commas. Any previous formatting is removed.
 *
 * @example
 * formatWithCommas('123456789') // '123,456,789'
 * formatWithCommas('1,200,300 SF') // '1,200,300'
 */
const formatWithCommas = (input: string) => {
  // Replaces non 0-9 numbers with ''
  input = input?.toString().replace(/[^\d.-]/g, '');

  // If after that replacement if there is nothing to format, return the blank string
  if (!input?.length) {
    return '';
  }

  const asNumber = Number(input);
  // Use integers as they are. Round decimals to two places.
  const rounded = (parseInt(input) === asNumber) ? asNumber : asNumber.toFixed(2);
  return rounded.toLocaleString();
};

/**
 * Given a string, returns only the numeric characters.
 *
 * @example
 * onlyNumericChars('123abc456') // '123456'
 * onlyNumericChars('abc') // ''
 * onlyNumericChars('$1,234.56') // '123456'
 */
const onlyNumericChars = (input: string) => {
  input = input?.toString().replace(/[^\d.-]/g, '');
  if (!input?.length) {
    return '';
  }
  return input;
};

/**
 * Converts radians to positive degrees.
 *
 * @example
 * radiansToPositiveDegrees(-(Math.PI / 2)) // 90
 * radiansToPositiveDegrees(1.57079632679) // 90
 */
const radiansToPositiveDegrees = (radians: number) => {
  let degrees = radians * 180 / Math.PI;
  while (degrees < 0) {
    degrees += 360;
  }
  return degrees;
};

/**
 * Attempts to return the value of a given string converted as a number. If the conversion fails, the original value is
 * returned. Only number strings will pass the conversion.
 *
 * @example
 * tryNumberConvert('123') // 123
 * tryNumberConvert('abc') // 'abc'
 * tryNumberConvert('123abc') // '123abc'
 * tryNumberConvert(123) // 123
 * tryNumberConvert(null) // null
 */
const tryNumberConvert = (value: string | number | null) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    // Using Number() because parseFloat() is ass and will convert '123abc' to 123, which is not desired
    const num = Number(value);
    return isNaN(num) ? value : num;
  }
  return value;
};

/**
 * Capitalizes the first letter of a string.
 *
 * @example
 * capitalize('hello') // 'Hello'
 */
const capitalize = (str: string) => {
  if (!str || str.length === 0) return;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Removes all whitespace and lowercases all characters within a given string.
 *
 * @example
 * trimStringToLower('   HELLO WorlD ') // 'helloworld'
 */
const trimStringToLower = (str: string) => {
  if (!str || str.length === 0) return;
  str.replace(/\s/g, '').toLowerCase();
};

/**
 * Prevents text from overflowing its container width by clipping it until it fits
 * without clipping the middle of the letters.
 */
const preventLetterClipping = (ref: VueElement, textContainerWidth: number) => {
  if (!ref) return;

  // Check if the text overflows
  if (ref.scrollWidth > textContainerWidth) {
    let clippedText = ref.textContent;

    // Iterate and clip the text until it fits
    while (ref.scrollWidth > textContainerWidth) {
      clippedText = clippedText.slice(0, -1);
      ref.textContent = clippedText;
    }
  }
};

export {
  formatWithCommas,
  onlyNumericChars,
  radiansToPositiveDegrees,
  tryNumberConvert,
  capitalize,
  trimStringToLower,
  preventLetterClipping,
};
